import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Loader2, Megaphone, X } from 'lucide-react';
import { GRAD } from '@/features/landing/components/theme';
import { clearClientSessionAndRedirect } from '@/shared/lib/auth/clearClientSession';
import { personalAdvertiserSignupPath } from '@/shared/config/roles';

type ClientPersonalAdvertiserPromptProps = {
  variant?: 'banner' | 'card' | 'modal';
  className?: string;
  onCancel?: () => void;
};

/**
 * Shown when an agency CLIENT tries to create/run their own campaigns.
 * Points them to register as a Personal Advertiser (requires logout).
 */
export function ClientPersonalAdvertiserPrompt({
  variant = 'banner',
  className = '',
  onCancel,
}: ClientPersonalAdvertiserPromptProps) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (!confirmOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLeaving) setConfirmOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [confirmOpen, isLeaving]);

  const closeConfirm = () => {
    if (isLeaving) return;
    setConfirmOpen(false);
  };

  const handleConfirm = () => {
    if (isLeaving) return;
    setIsLeaving(true);
    const signupPath = personalAdvertiserSignupPath();
    clearClientSessionAndRedirect(signupPath);
    navigate(signupPath, { replace: true });
  };

  const handleCancel = () => {
    if (isLeaving) return;
    if (onCancel) onCancel();
    else closeConfirm();
  };

  const actions = (
    <div className="flex items-center justify-end gap-3">
      <button
        type="button"
        disabled={isLeaving}
        onClick={handleCancel}
        className="rounded-[10px] border border-[var(--dash-line)] bg-white px-[18px] py-[9px] text-[13px] font-semibold text-[var(--dash-ink)] hover:bg-[var(--dash-soft)] disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="button"
        disabled={isLeaving}
        onClick={handleConfirm}
        className="inline-flex min-w-[108px] items-center justify-center gap-2 rounded-[10px] border-none px-[18px] py-[9px] text-[13px] font-semibold text-white transition hover:brightness-105 disabled:opacity-60"
        style={{
          background: GRAD,
          boxShadow: '0 8px 18px -10px rgba(0,201,201,0.55)',
        }}
      >
        {isLeaving ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        ) : (
          'Confirm'
        )}
      </button>
    </div>
  );

  const confirmBody = (
    <>
      <p className="m-0 text-[11px] font-semibold tracking-[0.12em] text-[var(--dash-brand)] uppercase">
        Personal advertiser
      </p>
      <h2 className="mt-1.5 mb-0 text-2xl font-semibold tracking-tight text-[var(--dash-ink)]">
        Log out to continue?
      </h2>
      <p className="mt-3 mb-0 text-sm leading-relaxed text-[var(--dash-muted)]">
        Confirming will log you out of this client account and open the
        Personal Advertiser registration page. You can sign back in anytime.
      </p>
    </>
  );

  if (variant === 'modal') {
    return (
      <div className={`text-left ${className}`}>
        {confirmBody}
        <div className="mt-6">{actions}</div>
      </div>
    );
  }

  const confirmModal =
    confirmOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="fixed inset-0 z-[120] flex items-end justify-center p-0 sm:items-center sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="personal-advertiser-confirm-title"
          >
            <button
              type="button"
              className="absolute inset-0 border-none bg-[#0F1B2D]/50 backdrop-blur-[2px]"
              aria-label="Close"
              onClick={closeConfirm}
            />
            <div className="camp-fade-up relative z-10 w-full max-w-md overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
              <div
                className="h-1.5 w-full"
                style={{ background: 'var(--dash-gradient)' }}
              />
              <button
                type="button"
                onClick={closeConfirm}
                disabled={isLeaving}
                className="absolute top-4 right-4 z-20 rounded-full p-2 text-[var(--dash-muted)] hover:bg-[var(--dash-soft)] disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="px-5 pt-7 pb-6 text-left sm:px-8 sm:pt-8 sm:pb-8">
                <div id="personal-advertiser-confirm-title">{confirmBody}</div>
                <div className="mt-6">{actions}</div>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  if (variant === 'card') {
    return (
      <>
        <div className={`flex flex-col items-center text-center ${className}`}>
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]">
            <Megaphone className="h-6 w-6" />
          </div>
          <h3 className="m-0 text-lg font-semibold text-[var(--dash-ink)]">
            Run ads on your own?
          </h3>
          <p className="mt-2 mb-5 max-w-sm text-sm leading-relaxed text-[var(--dash-muted)]">
            If you want to run the ads by yourself, register yourself as a
            personal advertiser.
          </p>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="dash-btn-gradient inline-flex shrink-0 items-center justify-center rounded-full px-4 py-2 text-xs font-semibold text-white"
          >
            Register as Personal Advertiser
          </button>
        </div>
        {confirmModal}
      </>
    );
  }

  return (
    <>
      <div
        className={`flex flex-col gap-3 rounded-2xl border border-[var(--dash-line)] bg-[var(--dash-soft)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${className}`}
      >
        <p className="m-0 text-left text-sm text-[var(--dash-muted)]">
          If you want to run the ads by yourself, register yourself as a{' '}
          <span className="font-semibold text-[var(--dash-ink)]">
            personal advertiser
          </span>
          .
        </p>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          className="dash-btn-gradient inline-flex shrink-0 items-center justify-center rounded-full px-4 py-2 text-xs font-semibold text-white"
        >
          Register as Personal Advertiser
        </button>
      </div>
      {confirmModal}
    </>
  );
}
